import React, { useState } from 'react';
import { Facebook, Twitter, Linkedin, Link2, Check, Share2, Mail } from 'lucide-react';

/**
 * Social media share buttons for news articles.
 * Uses native share intents (no third-party SDKs).
 */
export const SocialShare = ({ title, url }) => {
  const [copied, setCopied] = useState(false);

  // Resolve final URL on the client (so we always have absolute share URL)
  const shareUrl =
    url ||
    (typeof window !== 'undefined' ? window.location.href : '');
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title || '');

  const links = [
    {
      name: 'Facebook',
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      hoverColor: 'hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2]',
      testId: 'share-facebook',
    },
    {
      name: 'X (Twitter)',
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      hoverColor: 'hover:bg-black hover:text-white hover:border-black',
      testId: 'share-twitter',
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      hoverColor: 'hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2]',
      testId: 'share-linkedin',
    },
    {
      name: 'E-post',
      icon: Mail,
      url: `mailto:?subject=${encodedTitle}&body=${encodedTitle}%0A%0A${encodedUrl}`,
      hoverColor: 'hover:bg-slate-700 hover:text-white hover:border-slate-700',
      testId: 'share-email',
    },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // No-op: clipboard not available
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url: shareUrl });
      } catch {
        // User cancelled
      }
    }
  };

  return (
    <div
      data-testid="social-share"
      className="bg-white rounded-xl p-6 my-10 border border-slate-200 dark:border-slate-700"
    >
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-slate-900 font-bold">
          <Share2 className="w-5 h-5 text-green-500" />
          Del denne saken
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {links.map((link) => {
            const Icon = link.icon;
            return (
              <a
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Del på ${link.name}`}
                data-testid={link.testId}
                className={`inline-flex items-center justify-center w-10 h-10 rounded-full border border-slate-200 text-slate-600 transition-all duration-200 ${link.hoverColor}`}
              >
                <Icon className="w-4 h-4" />
              </a>
            );
          })}

          <button
            type="button"
            onClick={handleCopy}
            aria-label="Kopier lenke"
            data-testid="share-copy-link"
            className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-slate-200 text-slate-600 hover:bg-green-500 hover:text-black hover:border-green-500 transition-all duration-200"
          >
            {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
          </button>

          {typeof navigator !== 'undefined' && navigator.share && (
            <button
              type="button"
              onClick={handleNativeShare}
              aria-label="Del via enheten"
              data-testid="share-native"
              className="inline-flex items-center gap-2 px-3 h-10 rounded-full border border-slate-200 text-slate-600 hover:bg-green-500 hover:text-black hover:border-green-500 transition-all duration-200 text-sm font-medium"
            >
              <Share2 className="w-4 h-4" />
              Del
            </button>
          )}
        </div>
      </div>
      {copied && (
        <p className="text-green-600 text-sm mt-3" data-testid="share-copied-message">
          Lenken er kopiert!
        </p>
      )}
    </div>
  );
};

export default SocialShare;
